
import React from 'react';
import { Heart, Globe, Briefcase, Zap, AlertTriangle, Target, Brain, Radar, Trophy, ArrowRight } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';

interface Props {
    type: 'passion' | 'mission' | 'profession' | 'vocation' | 'drains' | 'quest' | 'fact' | 'alignment' | 'milestone';
    items: string[];
}

export const InsightCard: React.FC<Props> = ({ type, items }) => {
    const { theme } = useTheme();
    const isDark = theme.mode === 'dark';

    // Configuration map for metadata
    const meta = {
        passion: { icon: <Heart size={14} fill="currentColor" />, label: "CORE PASSION", color: "rose" },
        mission: { icon: <Globe size={14} />, label: "MISSION VECTOR", color: "purple" },
        profession: { icon: <Zap size={14} fill="currentColor" />, label: "SKILL ACQUIRED", color: "sky" },
        vocation: { icon: <Briefcase size={14} fill="currentColor" />, label: "MARKET VIABILITY", color: "emerald" },
        drains: { icon: <AlertTriangle size={14} />, label: "ENERGY LEAK", color: "red" },
        quest: { icon: <Target size={14} />, label: "MICRO QUEST", color: "amber" },
        fact: { icon: <Brain size={14} />, label: "MEMORY ENGRAM", color: "indigo" },
        alignment: { icon: <Radar size={14} />, label: "ALIGNMENT SHIFT", color: "cyan" },
        milestone: { icon: <Trophy size={14} />, label: "SYSTEM MILESTONE", color: "yellow" }
    };

    const { icon, label, color } = meta[type];

    // Dynamic Style Generator
    const getStyles = () => {
        if (isDark) {
            return {
                // Dark Mode: Deep, rich background
                cardBg: `bg-slate-950`, 
                border: `border-${color}-500/30`,
                accentBorder: `border-l-${color}-500`, // Solid accent
                headerText: `text-${color}-300`,
                bodyText: `text-slate-300`,
                iconBox: `bg-${color}-500/20 text-${color}-300`,
                bullet: `bg-${color}-500`,
                hoverRing: `group-hover:ring-${color}-500/30`
            };
        } else {
            return {
                // Light Mode: Solid Tinted Surface
                cardBg: `bg-${color}-50`, 
                border: `border-${color}-200`,
                accentBorder: `border-l-${color}-500`, // Solid accent
                headerText: `text-${color}-800`,
                bodyText: `text-${color}-900`,
                iconBox: `bg-white text-${color}-600 shadow-sm`,
                bullet: `bg-${color}-500`,
                hoverRing: `group-hover:ring-${color}-400/30`
            };
        }
    };

    const style = getStyles();

    return (
        <div className="w-full my-3 animate-scale-up origin-left">
            <div className={`
                relative overflow-hidden rounded-2xl border shadow-sm transition-all duration-300 group
                flex flex-col border-l-[6px]
                ${style.cardBg}
                ${style.border}
                ${style.accentBorder}
                hover:shadow-md hover:scale-[1.01]
                hover:ring-1 ${style.hoverRing}
            `}>
                
                {/* 1. Header (Compact Tech HUD) */}
                <div className="flex items-center justify-between px-4 py-2.5 border-b border-black/5 dark:border-white/5">
                    <div className="flex items-center gap-2">
                        <div className={`p-1 rounded ${style.iconBox}`}>
                            {icon}
                        </div>
                        <span className={`text-[10px] font-black tracking-[0.15em] uppercase truncate ${style.headerText}`}>
                            {label}
                        </span>
                    </div>
                    {/* Subtle Right Arrow hint */}
                    <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                        <ArrowRight size={12} className={style.headerText} />
                    </div>
                </div>

                {/* 2. Content Body (The Gem) */}
                <div className="p-4 pt-3">
                    <div className="space-y-2">
                        {items.map((item, idx) => (
                            <div key={idx} className="flex items-start gap-2.5">
                                <div className={`mt-1.5 w-1.5 h-1.5 rounded-full shrink-0 ${style.bullet} opacity-60`}></div>
                                <span className={`
                                    text-xs md:text-sm font-medium leading-relaxed
                                    ${type === 'quest' ? 'font-mono' : 'font-sans'}
                                    ${style.bodyText}
                                `}>
                                    {item}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>

            </div>
        </div>
    );
};
